import React, { useEffect, useState } from 'react';
import { Card, Table, Badge, Button, TextInput, Select, Modal, Textarea, Pagination, Radio, Label } from 'flowbite-react';
import { observer } from 'mobx-react-lite';
import { HiSearch, HiFilter, HiMail, HiRefresh, HiClock, HiTrash, HiClipboard, HiCheck } from 'react-icons/hi';
import { format } from 'date-fns';
import { registrationStore } from '../stores/registrationStore';
import Loading from '../components/Loading';
import NotificationHistoryModal from '../components/NotificationHistoryModal';

const RegistrationsPage: React.FC = observer(() => {
  const [isLoading, setIsLoading] = useState(true);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState<any>(null);
  const [message, setMessage] = useState('');
  const [searchType, setSearchType] = useState<'callNumber' | 'lineUserId'>('callNumber');
  const [searchValue, setSearchValue] = useState('');
  const [localStatus, setLocalStatus] = useState('');
  const [localFromDate, setLocalFromDate] = useState('');
  const [localToDate, setLocalToDate] = useState('');
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyLineUserId, setHistoryLineUserId] = useState<string | undefined>();
  const [copiedUid, setCopiedUid] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);

      // Set default date filter to today
      const today = format(new Date(), 'yyyy-MM-dd');
      setLocalFromDate(today);
      setLocalToDate(today);
      registrationStore.setFilterWithoutFetch('fromDate', today);
      registrationStore.setFilterWithoutFetch('toDate', today);

      await registrationStore.fetchRegistrations();
      setIsLoading(false);
    };
    fetchData();
  }, []);

  const handleSendMessage = async () => {
    if (!selectedRegistration || !message) return;

    const success = await registrationStore.sendMessage({
      lineUserId: selectedRegistration.lineUserId,
      message,
      registrationId: selectedRegistration.id,
      status: 'called',
    });

    if (success) {
      setShowMessageModal(false);
      setMessage('');
      setSelectedRegistration(null);
    }
  };

  const handleDeleteRegistration = async () => {
    if (!selectedRegistration) return;

    const success = await registrationStore.deleteRegistration(selectedRegistration.id);

    if (success) {
      setShowDeleteModal(false);
      setSelectedRegistration(null);
    }
  };

  const truncateLineUid = (uid: string): string => {
    if (!uid || uid.length <= 12) return uid;
    return `${uid.slice(0, 8)}...${uid.slice(-4)}`;
  };

  const handleCopyUid = (uid: string) => {
    navigator.clipboard.writeText(uid);
    setCopiedUid(uid);
    setTimeout(() => setCopiedUid(null), 1500);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'not-called': { color: 'gray', label: '未呼び出し' },
      'waiting': { color: 'yellow', label: '呼び出し待機中' },
      'calling': { color: 'blue', label: '呼び出し中' },
      'called': { color: 'green', label: '呼び出し済み' },
    };
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig['not-called'];
    return <Badge color={config.color as any}>{config.label}</Badge>;
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">ゲスト管理</h1>
          <p className="text-gray-600">登録されたゲストの一覧と手動メッセージ送信</p>
        </div>
        <Button onClick={() => registrationStore.fetchRegistrations()}>
          <HiRefresh className="mr-2 h-4 w-4" />
          更新
        </Button>
      </div>

      {/* フィルター */}
      <Card>
        <div className="flex flex-wrap gap-4 items-end">
          {/* 検索タイプ選択 */}
          <div className="flex gap-4 items-start flex-col">
            <div className="flex items-center gap-2">
              <Radio
                id="callNumber"
                name="searchType"
                value="callNumber"
                checked={searchType === 'callNumber'}
                onChange={() => setSearchType('callNumber')}
              />
              <Label htmlFor="callNumber">番号</Label>
            </div>
            <div className="flex items-center gap-2">
              <Radio
                id="lineUserId"
                name="searchType"
                value="lineUserId"
                checked={searchType === 'lineUserId'}
                onChange={() => setSearchType('lineUserId')}
              />
              <Label htmlFor="lineUserId">LINE UID</Label>
            </div>
          </div>

          {/* 検索入力 */}
          <div className="flex-1 min-w-[250px]">
            <TextInput
              icon={HiSearch}
              placeholder={searchType === 'callNumber' ? '番号で検索' : 'LINE UIDで検索'}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </div>
          <div className="flex-1 min-w-[200px]">
            <Select
              value={localStatus}
              onChange={(e) => setLocalStatus(e.target.value)}
            >
              <option value="">すべての最新ステータス</option>
              <option value="not-called">未呼び出し</option>
              <option value="waiting">呼び出し待機中</option>
              <option value="calling">呼び出し中</option>
              <option value="called">呼び出し済み</option>
            </Select>
          </div>
          <div className="min-w-[150px]">
            <label className="block text-xs text-gray-500 mb-1">開始日</label>
            <TextInput
              type="date"
              value={localFromDate}
              onChange={(e) => setLocalFromDate(e.target.value)}
            />
          </div>
          <div className="min-w-[150px]">
            <label className="block text-xs text-gray-500 mb-1">終了日</label>
            <TextInput
              type="date"
              value={localToDate}
              onChange={(e) => setLocalToDate(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={() => {
              // Apply all filters at once
              registrationStore.setFilterWithoutFetch('status', localStatus);
              registrationStore.setFilterWithoutFetch('fromDate', localFromDate);
              registrationStore.setFilterWithoutFetch('toDate', localToDate);
              registrationStore.setFilterWithoutFetch('searchType', searchType);
              registrationStore.setFilterWithoutFetch('searchValue', searchValue);
              registrationStore.fetchRegistrations();
            }}>
              <HiSearch className="mr-2 h-4 w-4" />
              検索
            </Button>
            <Button color="gray" onClick={() => {
              const today = format(new Date(), 'yyyy-MM-dd');
              setSearchValue('');
              setSearchType('callNumber');
              setLocalStatus('');
              setLocalFromDate(today);
              setLocalToDate(today);
              registrationStore.clearFilters();
            }}>
              <HiFilter className="mr-2 h-4 w-4" />
              クリア
            </Button>
          </div>
        </div>
      </Card>

      {/* テーブル */}
      <Card>
        <div className="overflow-x-auto">
          <Table hoverable>
            <Table.Head>
              <Table.HeadCell>番号</Table.HeadCell>
              <Table.HeadCell>LINE UID</Table.HeadCell>
              <Table.HeadCell>最新ステータス</Table.HeadCell>
              <Table.HeadCell>登録日時</Table.HeadCell>
              <Table.HeadCell>呼出日時</Table.HeadCell>
              <Table.HeadCell>アクション</Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {registrationStore.registrations
                .slice()
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                .map((registration) => (
                  <Table.Row key={registration.id} className="bg-white">
                    <Table.Cell className="font-medium">{registration.callNumber}</Table.Cell>
                    <Table.Cell>
                      <div className="flex items-center gap-1">
                        <span
                          className="text-xs text-gray-600 font-mono"
                          title={registration.lineUserId}
                        >
                          {truncateLineUid(registration.lineUserId)}
                        </span>
                        <button
                          onClick={() => handleCopyUid(registration.lineUserId)}
                          className="text-gray-400 hover:text-gray-600"
                          title="LINE UIDをコピー"
                        >
                          {copiedUid === registration.lineUserId
                            ? <HiCheck className="h-3 w-3 text-green-500" />
                            : <HiClipboard className="h-3 w-3" />
                          }
                        </button>
                      </div>
                    </Table.Cell>
                    <Table.Cell>{getStatusBadge(registration.status)}</Table.Cell>
                    <Table.Cell>
                      {registration.createdAt
                        ? format(new Date(registration.createdAt), 'yyyy/MM/dd HH:mm:ss')
                        : '-'}
                    </Table.Cell>
                    <Table.Cell>
                      {registration.calledAt
                        ? format(new Date(registration.calledAt), 'yyyy/MM/dd HH:mm:ss')
                        : '-'}
                    </Table.Cell>
                    <Table.Cell>
                      <div className="flex gap-2">
                        <Button
                          size="xs"
                          color="gray"
                          onClick={() => {
                            setHistoryLineUserId(registration.lineUserId);
                            setShowHistoryModal(true);
                          }}
                        >
                          <HiClock className="mr-1 h-3 w-3" />
                          履歴
                        </Button>
                        <Button
                          size="xs"
                          onClick={() => {
                            setSelectedRegistration(registration);
                            setShowMessageModal(true);
                          }}
                        >
                          <HiMail className="mr-1 h-3 w-3" />
                          通知
                        </Button>
                        <Button
                          size="xs"
                          color="failure"
                          onClick={() => {
                            setSelectedRegistration(registration);
                            setShowDeleteModal(true);
                          }}
                        >
                          <HiTrash className="mr-1 h-3 w-3" />
                          解除
                        </Button>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}
            </Table.Body>
          </Table>
        </div>

        {/* ページネーション */}
        <div className="flex justify-center mt-4">
          <Pagination
            currentPage={registrationStore.pagination.page}
            totalPages={registrationStore.pagination.totalPages}
            onPageChange={(page) => registrationStore.setPage(page)}
          />
        </div>
      </Card>

      {/* メッセージ送信モーダル */}
      <Modal show={showMessageModal} onClose={() => setShowMessageModal(false)}>
        <Modal.Header>LINE通知送信</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600 mb-2">送信先</p>
              <p className="font-medium">番号: {selectedRegistration?.callNumber}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                メッセージ
              </label>
              <Textarea
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="送信するメッセージを入力してください"
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleSendMessage}>送信</Button>
          <Button color="gray" onClick={() => setShowMessageModal(false)}>
            キャンセル
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 通知履歴モーダル */}
      <NotificationHistoryModal
        isOpen={showHistoryModal}
        onClose={() => {
          setShowHistoryModal(false);
          setHistoryLineUserId(undefined);
        }}
        lineUserId={historyLineUserId}
      />

      {/* 登録解除確認モーダル */}
      <Modal show={showDeleteModal} onClose={() => setShowDeleteModal(false)} size="md">
        <Modal.Header>登録解除の確認</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              以下の登録を解除してもよろしいですか？
            </p>
            <div className="bg-gray-50 p-4 rounded">
              <p className="font-medium">番号: {selectedRegistration?.callNumber}</p>
              <p className="text-sm text-gray-600 mt-1">
                ステータス: {selectedRegistration && getStatusBadge(selectedRegistration.status)}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                登録日時: {selectedRegistration?.createdAt
                  ? format(new Date(selectedRegistration.createdAt), 'yyyy/MM/dd HH:mm:ss')
                  : '-'}
              </p>
            </div>
            <p className="text-sm text-red-600 font-medium">
              ⚠️ この操作は取り消せません。通知ステートがおかしくなった場合の復旧用です。
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button color="failure" onClick={handleDeleteRegistration}>
            解除する
          </Button>
          <Button color="gray" onClick={() => setShowDeleteModal(false)}>
            キャンセル
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
});

export default RegistrationsPage;